/**
 * STORE LOCATOR ("Find a Retailer") — logic for sections/store-locator.liquid
 *
 * Defines the <store-locator> custom element. All texts, colors and category
 * definitions come from the section via a JSON config block and <template>
 * elements, so this file contains no storefront copy. Retailer data is
 * fetched from the middleware endpoint below (wraps the Shopify Storefront
 * API; contract documented in ticket 004c). Maps: vendored Leaflet 1.9.4 on
 * OpenStreetMap tiles, lazy-loaded. Geocoding: Nominatim behind the
 * swappable geocodeQuery adapter.
 */

import { matchesMediaQuery } from 'theme';

const RETAILERS_ENDPOINT = 'https://api.tante-e.com/amoena/retailers?shop=' + encodeURIComponent(window.Shopify.shop);

if (!window.customElements.get('store-locator')) {
  const CACHE_TTL_MS = 15 * 60 * 1000;
  const EARTH_RADIUS_MILES = 3958.8;

  const MARKER_PIN_SVG =
    '<svg viewBox="0 0 24 24" width="32" height="42" aria-hidden="true"><path fill="currentColor" d="M12 1C7.6 1 4 4.6 4 9c0 5.9 8 14 8 14s8-8.1 8-14c0-4.4-3.6-8-8-8z"/><circle cx="12" cy="9" r="3" fill="#fff"/></svg>';

  const toBool = (value) => value === true || value === 'true' || value === 'TRUE' || value === '1' || value === 1;

  const toCoord = (value) => {
    const number = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(number) ? number : null;
  };

  const parseCategories = (raw) => {
    const parsed = {};

    if (raw && typeof raw === 'object') {
      for (const [key, value] of Object.entries(raw)) {
        const level = parseInt(value, 10);
        if (level > 0) parsed[key] = Math.min(level, 5);
      }
    } else if (typeof raw === 'string') {
      for (const pair of raw.split(',')) {
        const [key, value] = pair.split(':');
        const level = parseInt(value, 10);
        if (key && key.trim() && level > 0) parsed[key.trim()] = Math.min(level, 5);
      }
    }

    return parsed;
  };

  const haversineMiles = (origin, target) => {
    const toRadians = (degrees) => (degrees * Math.PI) / 180;
    const dLat = toRadians(target.lat - origin.lat);
    const dLng = toRadians(target.lng - origin.lng);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRadians(origin.lat)) * Math.cos(toRadians(target.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(a));
  };

  const assortmentScore = (retailer) => Object.values(retailer.categories).reduce((sum, value) => sum + value, 0);

  const categoryCount = (retailer) => Object.keys(retailer.categories).length;

  const format = (template, replacements) =>
    Object.entries(replacements).reduce(
      (output, [token, value]) => output.replaceAll(`{${token}}`, value),
      template || ''
    );

  /**
   * Geocoding adapter. Returns {lat, lng} or null when the query cannot be
   * resolved. Currently backed by Nominatim (OpenStreetMap); swap this
   * function for the Google Geocoding API if the client provides a key.
   */
  const geocodeQuery = async (query, signal) => {
    const params = new URLSearchParams({ q: query, format: 'json', limit: '1', countrycodes: 'us' });
    const response = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
      signal,
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return null;

    const [hit] = await response.json();
    if (!hit) return null;

    const lat = toCoord(hit.lat);
    const lng = toCoord(hit.lon);
    return lat !== null && lng !== null ? { lat, lng } : null;
  };

  class StoreLocator extends HTMLElement {
    #abortController;
    #config;
    #refs = {};
    #retailers = null;
    #retailersPromise = null;
    #leafletPromise = null;
    #searchCoords = null;
    #searchLabel = '';
    #radius = 150;
    #activeFilters = new Set();
    #hasSearched = false;
    #lastResults = null;
    #map = null;
    #markersLayer = null;
    #modalMap = null;
    #modalMarker = null;

    connectedCallback() {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.#config = JSON.parse(this.querySelector('[data-store-locator-config]').textContent);

      this.#refs = {
        form: this.querySelector('[data-search-form]'),
        input: this.querySelector('[data-search-input]'),
        submit: this.querySelector('[data-search-submit]'),
        useLocation: this.querySelector('[data-use-location]'),
        radiusInput: this.querySelector('[data-radius-input]'),
        radiusValue: this.querySelector('[data-radius-value]'),
        inlineError: this.querySelector('[data-inline-error]'),
        chips: this.querySelector('[data-chips]'),
        status: this.querySelector('[data-status]'),
        statusCount: this.querySelector('[data-status-count]'),
        layout: this.querySelector('[data-layout]'),
        results: this.querySelector('[data-results]'),
        mapColumn: this.querySelector('[data-map-column]'),
        map: this.querySelector('[data-map]'),
        modal: this.querySelector('[data-modal]'),
        modalName: this.querySelector('[data-modal-name]'),
        modalAddress: this.querySelector('[data-modal-address]'),
        modalGmaps: this.querySelector('[data-modal-gmaps]'),
        modalMap: this.querySelector('[data-modal-map]'),
        cardTemplate: this.querySelector('[data-template-card]'),
        pillTemplate: this.querySelector('[data-template-pill]'),
        emptyTemplate: this.querySelector('[data-template-empty]'),
      };

      this.#radius = parseInt(this.#refs.radiusInput.value, 10);
      this.#updateRadiusOutput();

      if (!('geolocation' in navigator)) {
        this.#refs.useLocation.hidden = true;
      }

      this.#refs.form.addEventListener(
        'submit',
        (event) => {
          event.preventDefault();
          this.#onSearch();
        },
        { signal }
      );

      this.#refs.useLocation.addEventListener('click', () => this.#onUseLocation(), { signal });

      this.#refs.radiusInput.addEventListener(
        'input',
        () => {
          this.#radius = parseInt(this.#refs.radiusInput.value, 10);
          this.#updateRadiusOutput();
        },
        { signal }
      );

      this.#refs.radiusInput.addEventListener('change', () => this.#refresh(), { signal });

      this.#refs.chips.addEventListener(
        'click',
        (event) => {
          const chip = event.target.closest('[data-filter]');
          if (chip) this.#onToggleChip(chip);
        },
        { signal }
      );

      this.#refs.results.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('[data-clear-filters]')) this.#onClearFilters();
        },
        { signal }
      );

      this.#refs.modal.addEventListener(
        'dialog:before-show',
        (event) => {
          const retailerId = event.detail?.activator?.dataset.retailerId;
          if (retailerId) this.#fillModal(retailerId);
        },
        { signal }
      );

      this.#refs.modal.addEventListener('dialog:after-show', () => this.#updateModalMap(), { signal });

      // Prefetch the dataset as soon as the visitor shows intent to search.
      this.#refs.input.addEventListener(
        'focus',
        () => {
          this.#loadRetailers().catch(() => {});
        },
        { once: true, signal }
      );

      // Initialize the sidebar map when the viewport crosses into desktop after a search.
      window.matchMedia(window.themeVariables.mediaQueries['md']).addEventListener(
        'change',
        (event) => {
          if (event.matches && this.#hasSearched) this.#updateMainMap();
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#map?.remove();
      this.#modalMap?.remove();
      this.#map = null;
      this.#modalMap = null;
      this.#modalMarker = null;
      this.#markersLayer = null;
    }

    /* -------------------------------------------------- SEARCH FLOW */

    async #onSearch() {
      const query = this.#refs.input.value.trim();

      if (!query) {
        this.#refs.input.focus();
        return;
      }

      this.#hideInlineError();
      this.#setBusy(true);

      try {
        await this.#loadRetailers();

        const usesStoredLocation = this.#searchCoords !== null && query === this.#config.strings.currentLocation;

        if (!usesStoredLocation) {
          const coords = await geocodeQuery(query, this.#abortController.signal);
          this.#searchCoords = coords; // null when the query cannot be resolved → no-results state
          this.#searchLabel = query;
        }

        this.#hasSearched = true;
        this.#refresh();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.#retailersPromise = null;
        this.#showInlineError(this.#config.strings.error);
      } finally {
        this.#setBusy(false);
      }
    }

    #onUseLocation() {
      this.#hideInlineError();
      this.#refs.useLocation.setAttribute('aria-busy', 'true');
      this.#loadRetailers().catch(() => {});

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.#refs.useLocation.removeAttribute('aria-busy');
          this.#searchCoords = { lat: position.coords.latitude, lng: position.coords.longitude };
          this.#searchLabel = this.#config.strings.currentLocation;
          this.#refs.input.value = this.#config.strings.currentLocation;
        },
        () => {
          this.#refs.useLocation.removeAttribute('aria-busy');
          this.#showInlineError(this.#config.strings.geolocationError);
        },
        { timeout: 8000, maximumAge: 60000 }
      );
    }

    #onToggleChip(chip) {
      const filter = chip.dataset.filter;
      const isPressed = chip.getAttribute('aria-pressed') === 'true';

      chip.setAttribute('aria-pressed', String(!isPressed));

      if (isPressed) {
        this.#activeFilters.delete(filter);
      } else {
        this.#activeFilters.add(filter);
      }

      this.#refresh();
    }

    #onClearFilters() {
      this.#activeFilters.clear();

      for (const chip of this.#refs.chips.querySelectorAll('[data-filter]')) {
        chip.setAttribute('aria-pressed', 'false');
      }

      this.#refresh();
    }

    #refresh() {
      if (!this.#hasSearched || !this.#retailers) return;
      this.#renderResults(this.#computeResults());
    }

    /* -------------------------------------------------- DATA */

    #loadRetailers() {
      if (this.#retailers) return Promise.resolve(this.#retailers);
      this.#retailersPromise ??= this.#fetchRetailers();
      return this.#retailersPromise;
    }

    async #fetchRetailers() {
      const cacheKey = `store-locator:v1:${RETAILERS_ENDPOINT}`;

      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey));
        if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
          this.#retailers = this.#normalize(cached.data);
          return this.#retailers;
        }
      } catch {
        // Ignore unreadable cache entries.
      }

      const response = await fetch(RETAILERS_ENDPOINT, { signal: this.#abortController.signal });

      if (!response.ok) {
        throw new Error(`Store locator: endpoint responded with ${response.status}`);
      }

      const payload = await response.json();

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: payload }));
      } catch {
        // Storage full or unavailable — caching is best-effort.
      }

      this.#retailers = this.#normalize(payload);
      return this.#retailers;
    }

    #normalize(payload) {
      const rows = Array.isArray(payload) ? payload : payload?.retailers;

      if (!Array.isArray(rows)) {
        throw new Error('Store locator: unexpected endpoint response shape');
      }

      return rows.map((row, index) => ({
        id: String(row.id ?? row.handle ?? index),
        name: row.name || '',
        address: row.address || '',
        city: row.city || '',
        state: row.state || '',
        zip: row.zip || '',
        country: row.country || '',
        lat: toCoord(row.lat ?? row.latitude),
        lng: toCoord(row.lng ?? row.longitude),
        phone: row.phone || '',
        website: row.website || '',
        premier: toBool(row.premier),
        onlineOnly: toBool(row.onlineOnly ?? row.online_only),
        customFitting: toBool(row.customFitting ?? row.custom_fitting),
        categories: parseCategories(row.categories),
      }));
    }

    /* -------------------------------------------------- PIPELINE */

    #matchesFilters(retailer) {
      for (const filter of this.#activeFilters) {
        const [type, key] = filter.split(':');

        if (type === 'flag') {
          if (key === 'premier' && !retailer.premier) return false;
          if (key === 'custom_fitting' && !retailer.customFitting) return false;
          if (key === 'online' && !retailer.onlineOnly) return false;
        } else if ((retailer.categories[key] || 0) === 0) {
          return false;
        }
      }

      return true;
    }

    #computeResults() {
      if (!this.#searchCoords) {
        return { physical: [], online: [] };
      }

      const online = this.#config.hideOnline
        ? []
        : this.#retailers
            .filter((retailer) => retailer.onlineOnly)
            .filter((retailer) => this.#matchesFilters(retailer))
            .sort((a, b) => assortmentScore(b) - assortmentScore(a) || categoryCount(b) - categoryCount(a));

      const physical = this.#retailers
        .filter((retailer) => !retailer.onlineOnly && retailer.lat !== null && retailer.lng !== null)
        .filter((retailer) => this.#matchesFilters(retailer))
        .map((retailer) => ({ ...retailer, distance: haversineMiles(this.#searchCoords, retailer) }))
        .filter((retailer) => retailer.distance <= this.#radius)
        .sort((a, b) => b.premier - a.premier || a.distance - b.distance)
        .slice(0, this.#config.maxPhysical);

      return { physical, online };
    }

    /* -------------------------------------------------- RENDERING */

    #renderResults(results) {
      this.#lastResults = results;
      const all = [...results.physical, ...results.online];

      this.#refs.layout.hidden = false;
      this.#refs.results.replaceChildren();

      if (all.length === 0) {
        this.#refs.status.hidden = true;
        this.#refs.mapColumn.hidden = true;
        this.#refs.results.appendChild(this.#buildEmptyState());
        return;
      }

      this.#refs.status.hidden = false;
      this.#refs.statusCount.textContent = format(this.#config.strings.resultsCount, {
        count: all.length,
        location: this.#searchLabel,
      });

      const fragment = document.createDocumentFragment();
      for (const retailer of all) {
        fragment.appendChild(this.#buildCard(retailer));
      }
      this.#refs.results.appendChild(fragment);

      if (results.physical.length > 0 || this.#searchCoords) {
        this.#refs.mapColumn.hidden = false;
        this.#updateMainMap();
      } else {
        this.#refs.mapColumn.hidden = true;
      }
    }

    #buildCard(retailer) {
      const card = this.#refs.cardTemplate.content.cloneNode(true).firstElementChild;

      card.dataset.retailerId = retailer.id;
      if (retailer.premier) card.classList.add('store-locator__card--premier');
      if (retailer.onlineOnly) card.classList.add('store-locator__card--online');

      card.querySelector('[data-badge-premier]').hidden = !retailer.premier;
      card.querySelector('[data-badge-fitting]').hidden = !retailer.customFitting;
      card.querySelector('[data-badge-online]').hidden = !retailer.onlineOnly;
      card.querySelector('[data-badges]').hidden = !(
        retailer.premier ||
        retailer.customFitting ||
        retailer.onlineOnly
      );

      card.querySelector('[data-name]').textContent = retailer.name;

      if (retailer.onlineOnly) {
        card.querySelector('[data-distance-online]').hidden = false;
      } else if (typeof retailer.distance === 'number') {
        card.querySelector('[data-distance-physical]').hidden = false;
        card.querySelector('[data-distance-text]').textContent = format(this.#config.strings.distance, {
          distance: Math.round(retailer.distance),
        });
      } else {
        card.querySelector('[data-distance-row]').hidden = true;
      }

      const addressButton = card.querySelector('[data-address]');
      const fullAddress = this.#formatAddress(retailer);
      const canShowOnMap = retailer.lat !== null && retailer.lng !== null;

      if (fullAddress && canShowOnMap) {
        addressButton.textContent = fullAddress;
        addressButton.dataset.retailerId = retailer.id;
      } else {
        card.querySelector('[data-address-row]').hidden = true;
      }

      const onMapButton = card.querySelector('[data-on-map]');
      if (canShowOnMap) {
        onMapButton.dataset.retailerId = retailer.id;
      } else {
        onMapButton.hidden = true;
      }

      const pillsContainer = card.querySelector('[data-pills]');
      const pills = this.#buildPills(retailer);
      if (pills.childElementCount > 0) {
        pillsContainer.hidden = false;
        pillsContainer.appendChild(pills);
      }

      const phoneLink = card.querySelector('[data-phone]');
      if (retailer.phone) {
        phoneLink.hidden = false;
        phoneLink.href = `tel:${retailer.phone.replace(/[^0-9+]/g, '')}`;
        phoneLink.querySelector('[data-phone-text]').textContent = retailer.phone;
      }

      const websiteLink = card.querySelector('[data-website]');
      if (retailer.website) {
        websiteLink.hidden = false;
        websiteLink.href = retailer.website;
      }

      return card;
    }

    #buildPills(retailer) {
      const fragment = document.createDocumentFragment();

      for (const { key, label } of this.#config.categories) {
        const level = retailer.categories[key] || 0;
        if (level === 0) continue;

        const pill = this.#refs.pillTemplate.content.cloneNode(true).firstElementChild;

        pill.querySelector('[data-pill-label]').textContent = label;
        pill.setAttribute('aria-label', `${label}: ${level} out of 5`);

        const bars = pill.querySelectorAll('.store-locator__pill-bar');
        for (let index = 0; index < level; index += 1) {
          bars[index].classList.add('is-filled');
        }

        fragment.appendChild(pill);
      }

      return fragment;
    }

    #buildEmptyState() {
      const empty = this.#refs.emptyTemplate.content.cloneNode(true).firstElementChild;
      empty.querySelector('[data-empty-title]').textContent = format(this.#config.strings.noResultsTitle, {
        radius: this.#radius,
      });
      return empty;
    }

    #formatAddress(retailer) {
      const cityStateZip = [retailer.city, [retailer.state, retailer.zip].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');
      return [retailer.address, cityStateZip].filter(Boolean).join(', ');
    }

    /* -------------------------------------------------- MAPS */

    #ensureLeaflet() {
      if (window.L) return Promise.resolve(window.L);

      this.#leafletPromise ??= new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = this.#config.leafletUrl;
        script.onload = () => resolve(window.L);
        script.onerror = () => {
          this.#leafletPromise = null;
          reject(new Error('Store locator: could not load Leaflet'));
        };
        document.head.appendChild(script);
      });

      return this.#leafletPromise;
    }

    #createTileLayer(L) {
      return L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
      });
    }

    #createMarkerIcon(L, type) {
      return L.divIcon({
        className: `store-locator-marker store-locator-marker--${type}`,
        html: MARKER_PIN_SVG,
        iconSize: [32, 42],
        iconAnchor: [16, 42],
      });
    }

    async #updateMainMap() {
      if (!matchesMediaQuery('md') || !this.#lastResults) return;
      if (this.#lastResults.physical.length === 0 && !this.#searchCoords) return;

      let L;
      try {
        L = await this.#ensureLeaflet();
      } catch {
        this.#refs.mapColumn.hidden = true;
        return;
      }

      if (!this.isConnected) return;

      if (!this.#map) {
        this.#map = L.map(this.#refs.map, { scrollWheelZoom: false });
        this.#createTileLayer(L).addTo(this.#map);
        this.#markersLayer = L.layerGroup().addTo(this.#map);
      }

      this.#markersLayer.clearLayers();
      const bounds = [];

      if (this.#searchCoords) {
        const userLatLng = [this.#searchCoords.lat, this.#searchCoords.lng];
        L.marker(userLatLng, { icon: this.#createMarkerIcon(L, 'user'), zIndexOffset: 500 }).addTo(
          this.#markersLayer
        );
        bounds.push(userLatLng);
      }

      // Standard markers first so premier pins paint on top.
      const sorted = [...this.#lastResults.physical].sort((a, b) => a.premier - b.premier);

      for (const retailer of sorted) {
        const latLng = [retailer.lat, retailer.lng];
        const type = retailer.premier ? 'premier' : 'standard';
        const marker = L.marker(latLng, {
          icon: this.#createMarkerIcon(L, type),
          zIndexOffset: retailer.premier ? 250 : 0,
        }).addTo(this.#markersLayer);

        marker.on('click', () => this.#highlightCard(retailer.id));
        bounds.push(latLng);
      }

      this.#map.invalidateSize();

      if (bounds.length > 1) {
        this.#map.fitBounds(L.latLngBounds(bounds), { padding: [40, 40], maxZoom: 12 });
      } else if (bounds.length === 1) {
        this.#map.setView(bounds[0], 11);
      }
    }

    #highlightCard(retailerId) {
      const card = this.#refs.results.querySelector(`[data-retailer-id="${CSS.escape(retailerId)}"]`);
      if (!card) return;

      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      card.classList.add('store-locator__card--highlighted');
      setTimeout(() => card.classList.remove('store-locator__card--highlighted'), 1600);
    }

    #findRetailer(retailerId) {
      return this.#retailers?.find((retailer) => retailer.id === retailerId) || null;
    }

    #fillModal(retailerId) {
      const retailer = this.#findRetailer(retailerId);
      if (!retailer) return;

      this.#refs.modal.dataset.currentRetailerId = retailer.id;
      this.#refs.modalName.textContent = retailer.name;
      this.#refs.modalAddress.textContent = this.#formatAddress(retailer);

      const fullAddress = this.#formatAddress(retailer);
      const query = fullAddress ? `${retailer.name} ${fullAddress}` : `${retailer.lat},${retailer.lng}`;
      this.#refs.modalGmaps.href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

      this.#refs.modalMap.hidden = retailer.lat === null || retailer.lng === null;
    }

    async #updateModalMap() {
      const retailer = this.#findRetailer(this.#refs.modal.dataset.currentRetailerId);
      if (!retailer || retailer.lat === null || retailer.lng === null) return;

      let L;
      try {
        L = await this.#ensureLeaflet();
      } catch {
        this.#refs.modalMap.hidden = true;
        return;
      }

      const latLng = [retailer.lat, retailer.lng];
      const type = retailer.premier ? 'premier' : 'standard';

      if (!this.#modalMap) {
        this.#modalMap = L.map(this.#refs.modalMap, { scrollWheelZoom: false });
        this.#createTileLayer(L).addTo(this.#modalMap);
        this.#modalMarker = L.marker(latLng, { icon: this.#createMarkerIcon(L, type) }).addTo(this.#modalMap);
      } else {
        this.#modalMarker.setLatLng(latLng);
        this.#modalMarker.setIcon(this.#createMarkerIcon(L, type));
      }

      this.#modalMap.invalidateSize();
      this.#modalMap.setView(latLng, 14);
    }

    /* -------------------------------------------------- UI HELPERS */

    #updateRadiusOutput() {
      const { radiusInput, radiusValue } = this.#refs;
      const min = parseInt(radiusInput.min, 10);
      const max = parseInt(radiusInput.max, 10);
      const progress = ((this.#radius - min) / (max - min)) * 100;

      radiusInput.style.setProperty('--sl-range-progress', `${progress}%`);
      radiusValue.textContent = `${this.#radius} miles`;
    }

    #setBusy(isBusy) {
      this.#refs.submit.toggleAttribute('disabled', isBusy);
      if (isBusy) {
        this.#refs.submit.setAttribute('aria-busy', 'true');
      } else {
        this.#refs.submit.removeAttribute('aria-busy');
      }
    }

    #showInlineError(message) {
      this.#refs.inlineError.textContent = message;
      this.#refs.inlineError.hidden = false;
    }

    #hideInlineError() {
      this.#refs.inlineError.hidden = true;
    }
  }

  window.customElements.define('store-locator', StoreLocator);
}